import { EImportSectionsTarget } from '@constants';
import { ISection } from '@sdk/contracts';
import { Checkbox, Empty, Flex, Spin } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActivityItem } from './ActivityItem';
import {
  Wrapper,
  EmptyActivities,
  StyledCollapse,
  StyledTag,
  SelectAllWrapper
} from './SectionsList.styles';

import { Icon } from '@/components/Icon';
import { Box } from '@/components/Utils';
import { useSectionCourseDetail } from '@/store';

type Props = {
  courseId?: string;
  selectedSections: ISection[];
  onSelectionChange: (sections: ISection[]) => void;
  target: EImportSectionsTarget;
};

export const SectionsList: React.FC<Props> = ({
  courseId,
  selectedSections,
  onSelectionChange,
  target
}) => {
  const { t } = useTranslation('ManagerBaseContentDetailTranslations');
  const { course, isLoading } = useSectionCourseDetail(courseId);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const isSelected = (sectionId: string) => selectedSections.some(s => s._id === sectionId);

  const handleCheckboxChange = (section: ISection, checked: boolean) =>
    checked
      ? onSelectionChange([...selectedSections, section])
      : onSelectionChange(selectedSections.filter(s => s._id !== section._id));

  const availableSections =
    (target === EImportSectionsTarget.INTRODUCTION ? course?.introduction : course?.sections) ?? [];

  const allSelected = selectedSections.length === availableSections.length;
  const isIndeterminate =
    selectedSections.length > 0 && selectedSections.length < availableSections.length;

  const handleSelectAll = (checked: boolean) => {
    if (!availableSections.length) return;
    onSelectionChange(checked ? [...availableSections] : []);
  };

  if (!courseId) return null;

  if (isLoading) {
    return (
      <Wrapper>
        <Flex justify="center" align="center" style={{ padding: 32 }}>
          <Spin />
        </Flex>
      </Wrapper>
    );
  }

  if (!availableSections?.length) {
    return (
      <Wrapper>
        <Empty description={t('NO_SECTIONS_AVAILABLE')} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <SelectAllWrapper>
        <Checkbox
          checked={allSelected}
          indeterminate={isIndeterminate}
          onChange={e => handleSelectAll(e.target.checked)}
        />
        <div>{t('SELECT_ALL')}</div>
      </SelectAllWrapper>
      {availableSections.map((section, index) => {
        const isFirst = index === 0;
        const isLast = index === availableSections.length - 1;

        return (
          <StyledCollapse
            key={section._id}
            $isFirst={isFirst}
            $isLast={isLast}
            activeKey={activeKeys}
            onChange={keys => setActiveKeys(keys)}
            expandIcon={({ isActive }) => (
              <Box gap={10}>
                <Icon i={isActive ? 'CaretDown' : 'CaretRight'} size={16} />
              </Box>
            )}
            items={[
              {
                key: section._id,
                label: (
                  <Flex gap={8} align="center">
                    <Checkbox
                      checked={isSelected(section._id)}
                      onClick={e => e.stopPropagation()}
                      onChange={e => handleCheckboxChange(section, e.target.checked)}
                    />
                    <div>{section.name}</div>
                    <StyledTag>
                      {t('ACTIVITIES', { count: section?.activities?.length ?? 0 })}
                    </StyledTag>
                  </Flex>
                ),
                children: section.activities?.length ? (
                  section.activities.map(activity => (
                    <ActivityItem key={activity._id} activity={activity} />
                  ))
                ) : (
                  <EmptyActivities>{t('NO_ACTIVITIES_IN_SECTION')}</EmptyActivities>
                )
              }
            ]}
          />
        );
      })}
    </Wrapper>
  );
};
